import React, { useEffect, useState, forwardRef } from 'react'
import { EditorState, getDefaultKeyBinding } from 'draft-js'

/**
 * HOC that adds the following features/enhancements to a supplied Draftjs editor
 *  - Content is controlled with a string, rather than an EditorState, so the
 *    parent component does not need to do anything Draft related
 *    - Very similar to how a regular input is controlled
 *    - This does mean that (de)serialization functions are required
 *      - See ../videoSerialization for serializers to use when also using a loom HOC
 *      - Use the draft-js-import/export-html libraries for non-loom use cases
 *      - serializer can (optionally) accept a second boolean param that will be
 *        true when passing up the value to the parent component
 *        - allows seralizer to differentially clean up or leave raw for presenting
 *          the value to a parent vs storing for recovery (which would be handled
 *          by a different HOC)
 *  - Support for custom enter key handling (optional)
 *    - shift+enter will override custom handling so users can still add multiple lines
 *  - focusOnMount prop: when true, will automatically bring the editor into focus
 *    when it gets mounted
 *
 * If using with another Draft js HOC (like withLoomIntegration), it should
 * be applied last, as other HOCs will likely rely on (or at least assume) the
 * standard EditorState control
 */
const enhanceEditor = BaseEditor =>
  forwardRef((props, editor) => {
    const {
      value,
      setValue,
      initialValue,
      serialize,
      deserialize,
      onEnter,
      keyBindingFn,
      handleKeyCommand,
      focusOnMount
    } = props

    const [editorState, setEditorState] = useState(() =>
      initialValue || value
        ? EditorState.createWithContent(deserialize(initialValue || value))
        : EditorState.createEmpty()
    )

    const [localValue, setLocalValue] = useState(value)
    const [initialized, setInitialized] = useState(!focusOnMount)

    const onEditorChange = newState => {
      setEditorState(newState)

      const serialized = serialize(newState.getCurrentContent(), true)
      setLocalValue(serialized)
      setValue(serialized)
    }

    useEffect(() => {
      // Allow component to be controlled
      if (value !== localValue) {
        const newState = EditorState.createWithContent(deserialize(value))
        const newStateFixedSelection = EditorState.moveFocusToEnd(newState)
        onEditorChange(newStateFixedSelection)
      }
    }, [value])

    useEffect(() => {
      if (focusOnMount) {
        editor.current.focus()
        setInitialized(true)
      }
    }, [])

    const extendedKeyBindingFn = e => {
      if (onEnter && e.key === 'Enter' && !e.shiftKey) {
        return 'custom-on-enter'
      }
      return keyBindingFn ? keyBindingFn(e) : getDefaultKeyBinding(e)
    }

    const extendedHandleKeyCommand = command => {
      if (onEnter && command === 'custom-on-enter') {
        onEnter()
        return 'handled'
      } else if (handleKeyCommand) {
        return handleKeyCommand(command)
      }
      return 'not-handled'
    }

    return (
      <BaseEditor
        {...props}
        ref={editor}
        editorState={editorState}
        onChange={onEditorChange}
        keyBindingFn={extendedKeyBindingFn}
        handleKeyCommand={extendedHandleKeyCommand}
        initialized={initialized}
      />
    )
  })

export default enhanceEditor
